import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export enum UserGoalCategory {
  ONE_ON_ONE = 'ONE_ON_ONE',
  SALARY_RAISE = 'SALARY_RAISE',
  YEAR_GOALS = 'YEAR_GOALS',
  OTHER = 'OTHER',
}

export interface UserGoalsFilterPayload {
  dates?: [string, string];
}

export interface ManagerUserGoalsFilterPayload extends UserGoalsFilterPayload {
  collaboratorUuid?: string;
}

const transformUserGoals = (goals: hubClient.CollaboratorGoalDto[]): hubClient.CollaboratorGoalDto[] => {
  return goals.toSorted((a, b) => {
    if (a.done !== b.done) {
      return a.done ? 1 : -1;
    }

    if (!a.deadline && !b.deadline) {
      return 0;
    }
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;

    return new Date(b.deadline).getTime() - new Date(a.deadline).getTime();
  });
};

@Module({ name: 'UserGoalsModule', store, dynamic: true, namespaced: true })
export class UserGoalsModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _userGoals: hubClient.CollaboratorGoalDto[] = [];

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get userGoals(): hubClient.CollaboratorGoalDto[] {
    return this._userGoals;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  setUserGoals(userGoals: hubClient.CollaboratorGoalDto[]): void {
    this._userGoals = userGoals;
  }

  @Mutation
  clearUsers(): void {
    this._userGoals = [];
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchUserGoals(payload: ManagerUserGoalsFilterPayload): Promise<void> {
    this.setLoading(true);
    this.setUserGoals([]);
    const dates = payload.dates;
    const transformedFilter = {
      collaboratorUuid: payload.collaboratorUuid,
      fromDate: dates ? dates[0] : undefined,
      toDate: dates ? dates[1] : undefined,
    };

    try {
      const { data: response } = await hubClient.V3CollaboratorGoalsService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query: {
          limit: 999,
          offset: 0,
          fromDate: transformedFilter.fromDate,
          toDate: transformedFilter.toDate,
          collaboratorUuid: transformedFilter.collaboratorUuid,
        },
        throwOnError: true,
      });
      const userGoals: hubClient.CollaboratorGoalDto[] = transformUserGoals(response.data);
      this.setUserGoals(userGoals);
    } catch (err) {
      this.setError(true);
      console.log(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async fetchUserGoalsForUser(payload: {
    collaboratorUuid: string;
    filter?: UserGoalsFilterPayload;
  }): Promise<void> {
    this.setLoading(true);
    const dates = payload.filter?.dates;
    const transformedFilter = {
      fromDate: dates ? dates[0] : undefined,
      toDate: dates ? dates[1] : undefined,
    };

    try {
      const { data: response } = await hubClient.V3CollaboratorGoalsService.getManyForCollaboratorV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT, uuid: payload.collaboratorUuid },
        query: {
          limit: 999,
          offset: 0,
          fromDate: transformedFilter.fromDate,
          toDate: transformedFilter.toDate,
        },
        throwOnError: true,
      });
      const userGoals: hubClient.CollaboratorGoalDto[] = transformUserGoals(response.data);
      this.setUserGoals(userGoals);
    } catch (err) {
      this.setError(true);
      console.log(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(UserGoalsModule);
